<script setup lang="ts">
/**
 * ViewRecallModal — Displays recall details in a read-only modal.
 *
 * Shows full recall information and provides Approve / Reject CTAs
 * when the recall status is "Pending". For Approved or Rejected recalls
 * only a Close button is shown.
 */
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { DialogModal } from '@tms-phoenix/ui-vue';
import Button from 'primevue/button';
import type { Recall } from '../types';

const router = useRouter();

const props = defineProps<{
  visible: boolean;
  recall: Recall | null;
}>();

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void;
  (e: 'approve', recall: Recall): void;
  (e: 'reject', recall: Recall): void;
}>();

const isPending = computed(() => props.recall?.status === 'Pending');

function viewAffectedPatients() {
  if (!props.recall) return;
  emit('update:visible', false);
  router.push({ name: 'recall-impact', params: { id: props.recall.id } });
}
</script>

<template>
  <DialogModal
    :visible="visible"
    :title="$t('recall.viewModal.title')"
    max-width="max-w-2xl"
    position="center"
    :show-footer="true"
    @update:visible="emit('update:visible', $event)"
  >
    <div
      v-if="recall"
      class="flex flex-col gap-5"
    >
      <!-- Row: Recall Name & Recall Date -->
      <div class="grid grid-cols-2 gap-6">
        <div class="flex flex-col gap-1">
          <span class="text-xs font-medium text-gray-500">{{ $t('recall.column.recallName') }}</span>
          <span class="text-sm font-semibold text-gray-900">{{ recall.name }}</span>
        </div>
        <div class="flex flex-col gap-1">
          <span class="text-xs font-medium text-gray-500">{{ $t('recall.column.recallDate') }}</span>
          <span class="text-sm font-semibold text-gray-900">{{ recall.recallDate }}</span>
        </div>
      </div>

      <!-- Row: Product Name & Manufacturer -->
      <div class="grid grid-cols-2 gap-6">
        <div class="flex flex-col gap-1">
          <span class="text-xs font-medium text-gray-500">{{ $t('inventory.brandName') }}</span>
          <span class="text-sm font-semibold text-gray-900">{{ recall.productName }}</span>
        </div>
        <div class="flex flex-col gap-1">
          <span class="text-xs font-medium text-gray-500">{{ $t('ui.overview.manufacturer') }}</span>
          <span class="text-sm font-semibold text-gray-900">{{ recall.manufacturer ?? '—' }}</span>
        </div>
      </div>

      <!-- Affected Lot Numbers -->
      <div class="flex flex-col gap-1">
        <span class="text-xs font-medium text-gray-500">{{ $t('recall.viewModal.affectedLotNumbers') }}</span>
        <span class="text-sm font-bold text-gray-900">{{ recall.affectedLotNumbers ?? '—' }}</span>
      </div>

      <!-- Linked Catalog Item -->
      <!-- Shown for any recall that isn't Rejected/Cancelled — the affected-patients page
           is keyed by recall id, not by linkedProductId, so it must not gate visibility. -->
      <div
        v-if="recall.status !== 'Rejected' && recall.status !== 'Cancelled'"
        class="flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 px-4 py-3"
      >
        <div class="flex flex-col gap-0.5">
          <span class="text-xs font-medium text-gray-500">{{ $t('recall.viewModal.linkedCatalogItem') }}</span>
          <span class="text-sm font-semibold text-gray-900">{{ recall.productName || $t('recall.viewModal.productIdPrefix', { id: recall.linkedProductId }) }}</span>
        </div>
        <Button
          :label="$t('recall.viewModal.viewAffectedPatients')"
          text
          class="p-0! text-sm! font-semibold! whitespace-nowrap"
          @click="viewAffectedPatients"
        />
      </div>

      <!-- Clinical Description -->
      <div class="flex flex-col gap-1">
        <span class="text-xs font-medium text-gray-500">{{ $t('recall.viewModal.clinicalDescription') }}</span>
        <p class="text-sm text-gray-700 leading-relaxed">
          {{ recall.clinicalDescription ?? '—' }}
        </p>
      </div>

      <!-- Row: Information Source & Source URL -->
      <div class="grid grid-cols-2 gap-6 border-t border-gray-200 pt-4">
        <div class="flex flex-col gap-1">
          <span class="text-xs font-medium text-gray-500">{{ $t('recall.viewModal.informationSource') }}</span>
          <span class="text-sm font-semibold text-gray-900">{{ recall.informationSource ?? '—' }}</span>
        </div>
        <div class="flex flex-col gap-1">
          <span class="text-xs font-medium text-gray-500">{{ $t('recall.viewModal.sourceUrl') }}</span>
          <a
            v-if="recall.sourceUrl"
            :href="recall.sourceUrl.startsWith('http') ? recall.sourceUrl : `https://${recall.sourceUrl}`"
            target="_blank"
            rel="noopener noreferrer"
            class="text-sm font-semibold text-blue-600 hover:underline break-all"
          >
            {{ recall.sourceUrl }}
          </a>
          <span
            v-else
            class="text-sm text-gray-900"
          >—</span>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <template #footer>
      <template v-if="isPending && recall">
        <Button
          :label="$t('recall.viewModal.approveLockdown')"
          severity="primary"
          @click="emit('approve', recall)"
        />
        <Button
          :label="$t('recall.viewModal.rejectRequest')"
          severity="secondary"
          outlined
          @click="emit('reject', recall)"
        />
      </template>
      <template v-else>
        <Button
          :label="$t('common.close')"
          severity="secondary"
          outlined
          @click="emit('update:visible', false)"
        />
      </template>
    </template>
  </DialogModal>
</template>
